import React, { useEffect, useState } from 'react';
import 'react-circular-progressbar/dist/styles.css';
import {
  CircularProgressbarWithChildren,
  buildStyles,
} from 'react-circular-progressbar';
import { SectionHead } from '../../../../components/SectionHead/SectionHead';
import { getUserQuizAssessmentStats } from './api';
import './QuizAssessment.scss';
import AppLink from '../../../../components/AppLink';
import { mockData } from './mock';
import CountUp from 'react-countup';
import Tabs from './components/Tabs';
import { ProgressProvider } from './helpers';

const INITIAL_DATA = mockData()

const QuizAssessment = ({ sectionData, isWidget = false }) => {
  const [categories, setCategories] = useState({});
  const [tabs, setTabs] = useState({});
  const [data, setData] = useState(INITIAL_DATA);
  const [activeTab, setActiveTab] = useState("preflop");

  const getStats = async () => {
    try {
      const data = await getUserQuizAssessmentStats();
      setData(data);
    } catch (e) {
      console.log(e);
    }
  };

  useEffect(() => {
    getStats();
  }, []);

  const populateData = (data, isTabs = false) => {
    const categories = data.reduce((acc, curr) => {
      const {
        category,
        subcategory,
        started,
        stats,
        post_name,
        total,
        percent,
        questions_count: totalCount,
      } = curr;
      const { total: completed, correct_count: correct } = stats || {};

      const categoryLabel = isTabs ? 'stats' : category;

      const subcategoryLabel = isTabs ? category : subcategory;

      const categoryData = acc[categoryLabel] || {};
      const subcategoryData = categoryData[subcategoryLabel] || {
        completed: 0,
        correct: 0,
        totalCount: 0,
      };

      const link = isTabs
        ? `/quiz-assessment/`
        : `/courses/quiz-assesment/${category}-assesment/quizz/${post_name}/`;

      return {
        ...acc,
        [categoryLabel]: {
          ...categoryData,
          [subcategoryLabel || 'default']: {
            completed: subcategoryData.completed + (completed || 0),
            correct: subcategoryData.correct + (correct || 0),
            category,
            post_name,
            link,
            total,
            percent,
            totalCount: subcategoryData.totalCount + totalCount,
          },
        },
      }
    }, {});
    return categories;
  };

  useEffect(() => {
    if (!data?.length) return;
    const categories = populateData(data);
    const tabs = populateData(data, true);
    setCategories(categories);
    setTabs(tabs);
  }, [data]);

  const getColor = (percentage) => {
    if (percentage === 0) {
      return {
        pathColor: 'rgb(128,128,128)',
        trailColor: 'rgba(128,128,128,0.3)',
      };
    }
    if (percentage > 75)
      return {
        pathColor: 'rgb(50,205,50)',
        trailColor: 'rgba(50,205,50,0.3)',
      };
    if (percentage > 25)
      return {
        pathColor: 'rgb(255,215,0)',
        trailColor: 'rgba(255,215,0,0.3)',
      };
    return {
      pathColor: 'rgb(255,0,0)',
      trailColor: 'rgba(255,0,0,0.3)',
    };
  };

  const renderSubcategories = (categoryData) => {
    return (
      <div className="QuizAssessment__subcategories-container"
      >
        {Object.entries(categoryData).map(
          ([subcategoryName, subcategoryData]) => {
            const { completed, correct, link } = subcategoryData
            const percentage = Math.round((correct / completed) * 100) || 0
            const { trailColor, pathColor } = getColor(percentage)
            return (
              <div key={subcategoryName} className="QuizAssessment__slider-item">
                <AppLink to={link}>
                  <div className="QuizAssessment__slider-wrapper">
                    <div className="QuizAssessment__slider-title">
                      {subcategoryName.replaceAll('-', ' ')}
                    </div>
                    <div className="QuizAssessment__slider-box">
                      <ProgressProvider valueStart={0} valueEnd={percentage}>
                        {(value) => (
                          <CircularProgressbarWithChildren
                            strokeWidth={16}
                            value={value}
                            styles={buildStyles({
                              // How long animation takes to go from one percentage to another, in seconds
                              pathTransitionDuration: 2,
                              // Colors
                              pathColor,
                              trailColor,
                            })}
                          >
                            <div className="QuizAssessment__circular-progress-bar">
                              <CountUp duration={2} start={0} end={value} />%
                            </div>
                          </CircularProgressbarWithChildren>
                        )}
                      </ProgressProvider>
                      <div className="QuizAssessment__box-stats">
                        <div className="QuizAssessment__box-stats-actions">
                          ACCURACY:
                        </div>
                        <div className="QuizAssessment__box-stats-actions">
                          <CountUp duration={2} start={0} end={correct} /> / 
                          <div>{completed}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </AppLink>
              </div>
            )
          },
        )}
      </div>
    )
  }

  const renderCategories = () => {
    if (!Object.keys(categories).length) {
      return (
        <div className="QuizAssessment__box-start">
          Take <AppLink to="/courses/quiz-assessment/">Quiz Assessment</AppLink>{' '}
          to see your progress.
        </div>
      )
    }
    return Object.entries(categories).map(([categoryName, categoryData]) => {
      if (categoryName !== activeTab) return null
      return (
        <div key={categoryName}>
          {!isWidget && (
            <div id={categoryName} className="QuizAssessment__categories">
              {categoryName} Analysis
            </div>
          )}
          {renderSubcategories(categoryData)}
        </div>
      );
    });
  };

  const renderData = () => {
    if (isWidget) {
      return <Tabs tabs={tabs}/>
    }
    return (
      <div>
        <Tabs activeTab={activeTab} setActiveTab={setActiveTab} tabs={tabs} isWidget/>
        {renderCategories()}
      </div>
    )
  }

  return (
    <div className="QuizAssessment">
      <div className="QuizAssessment__categories-margin">
        {!isWidget && (
          <>
            <h1 className="QuizAssessment__title">Quiz Assessment Analysis</h1>
            <div className="QuizAssessment__breadcrumbs">
              <AppLink to="/dashboard/">Home</AppLink>
              <span>&nbsp;&rsaquo;&nbsp;</span>
              <AppLink to="/courses/quiz-assessment/">Quiz Assessment</AppLink>
              <span>&nbsp;&rsaquo;&nbsp;</span>
              <span>Analysis</span>
            </div>
          </>
        )}
        {isWidget && (
          <SectionHead
            title={sectionData?.title}
            titleUppercase
            action={{
              type: 'link',
              route: sectionData?.react_route,
              children: sectionData?.more,
            }}
          />
        )}
        {renderData()}
      </div>
    </div>
  );
};

export default QuizAssessment;
